import React, { LegacyRef, useMemo } from 'react';
import {
  Combobox,
  Flex,
  LucideIconName as IconName,
  SelectionTile,
} from '@highereducation/hen-ui';
import { QuestionProps } from '@de/components/Step/Step';
import { Option } from '@de/types/types';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import useQuestionEvents from '@de/hooks/useQuestionEvents';
import { OptionsLoadingPlaceholder } from '@de/components/Step/OptionsLoadingPlaceholder';
import { ErrorMessage } from '@de/components/ErrorMessage';
import {
  optionToComboboxOption,
  partitionSearchBarOptions,
} from '@de/utils/searchBarOptions';

const SingleChoiceTilesQuestion: React.FC<QuestionProps> = ({
  options = [],
  id = '',
  leadFieldAssociated,
  automaticNext = false,
  isMultiQuestionChild = false,
  title,
  stepName,
  variant,
  isOptionsLoading = false,
  searchBarAllOptions,
  dynamicOptionsError,
}) => {
  const { updateFlowState, getStepMetadataById, handleStepForward } =
    useDynamicExperienceContext();
  const stepSelectedData = getStepMetadataById(id || '');
  const selectedOption = stepSelectedData?.option as Option;

  const { ref: visibilityRef, triggerFieldInputted } = useQuestionEvents({
    id,
    fieldType: 'single_choice',
    options,
    title,
    stepName,
    isMultiQuestionChild,
    variant: variant || 'single_choice_tiles',
    leadFieldAssociated,
  });

  const hasSearchBar = Boolean(searchBarAllOptions?.length);

  const isSingleSelected =
    selectedOption && !Array.isArray(selectedOption)
      ? selectedOption
      : undefined;

  const { displayedOptions, searchableOptions } = useMemo(() => {
    if (!hasSearchBar) {
      return { displayedOptions: options, searchableOptions: [] as Option[] };
    }
    return partitionSearchBarOptions(
      options,
      searchBarAllOptions as Option[],
      isSingleSelected ? [isSingleSelected] : [],
    );
  }, [hasSearchBar, options, searchBarAllOptions, isSingleSelected]);

  const selectOption = (option: Option) => {
    updateFlowState(id, { option, leadFieldAssociated });
    if (automaticNext && !isMultiQuestionChild) {
      handleStepForward();
    }
    triggerFieldInputted(option.text as string);
  };

  const handleSearchSelect = (value: string) => {
    const option = (searchBarAllOptions ?? []).find(
      (opt) => opt.optionId === value,
    );
    if (option) {
      selectOption(option);
    }
  };

  const showLoading = isOptionsLoading && !dynamicOptionsError;

  return (
    <>
      {dynamicOptionsError ? (
        <ErrorMessage
          message={dynamicOptionsError}
          className='w-full justify-center py-2 px-2'
        />
      ) : null}
      <Flex
        gap='lg'
        wrap='wrap'
        justify='center'
        direction='column'
        className='pb-2 md:flex-row max-w-full'
        ref={visibilityRef as LegacyRef<HTMLDivElement>}
      >
        {showLoading ? (
          <OptionsLoadingPlaceholder />
        ) : (
          displayedOptions.map((option) => {
            const isSelected = isSingleSelected?.optionId === option.optionId;

            const icon = option.icon as IconName;

            return (
              <SelectionTile
                key={option.optionId}
                icon={icon}
                label={option.text}
                description={option.subtext}
                isSelected={isSelected}
                variant='secondary'
                alignment='center'
                className='flex-1'
                onClick={() => {
                  if (isSelected) {
                    updateFlowState(id, null);
                  } else {
                    updateFlowState(id, { option, leadFieldAssociated });
                    if (automaticNext && !isMultiQuestionChild) {
                      handleStepForward();
                    }
                  }

                  triggerFieldInputted(option.text as string);
                }}
              />
            );
          })
        )}
      </Flex>
      {!showLoading && hasSearchBar && searchableOptions.length > 0 && (
        <Combobox
          className='mt-4'
          aria-label={title || 'Search options'}
          options={searchableOptions.map(optionToComboboxOption)}
          selectedValues={isSingleSelected ? [isSingleSelected.optionId] : []}
          onSelect={handleSearchSelect}
        />
      )}
    </>
  );
};

export default SingleChoiceTilesQuestion;
